<template>
  <el-dialog
    v-model="visible"
    :title="isEdit ? 'Edit Campaign Group' : 'Campaign Group'"
    width="600px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <el-form-item label="Name" prop="name">
        <el-input v-model="form.name" placeholder="Enter group name" />
      </el-form-item>

      <el-form-item label="Campaign" prop="campaign_ids">
        <el-select
          v-model="form.campaign_ids"
          multiple
          filterable
          remote
          reserve-keyword
          placeholder="Search by ID or name"
          :remote-method="searchCampaigns"
          :loading="searchLoading"
          style="width: 100%"
        >
          <el-option
            v-for="campaign in campaignOptions"
            :key="campaign.id"
            :label="`${campaign.attributes.id} ${campaign.attributes.name}`"
            :value="campaign.attributes.id"
          />
        </el-select>
      </el-form-item>
    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">Cancel</el-button>
        <el-button type="primary" :loading="loading" @click="handleSave">
          Save
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, watch, computed, PropType } from 'vue'
import { ElMessage } from 'element-plus'
import { useCampaignGroupsStore } from '@/apps/traforama/store/campaignGroups'
import {
  ICampaignGroup,
  ICampaignMini
} from '@/apps/traforama/interfaces/campaignGroups'

export default defineComponent({
  name: 'CampaignGroupModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    editGroup: {
      type: Object as PropType<ICampaignGroup | null>,
      default: null
    }
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const store = useCampaignGroupsStore()
    const formRef = ref()
    const loading = ref(false)

    const form = reactive({
      name: '',
      campaign_ids: [] as number[]
    })

    const rules = {
      name: [
        { required: true, message: 'Please enter group name', trigger: 'blur' }
      ]
    }

    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const isEdit = computed(() => !!props.editGroup)

    const searchLoading = computed(() => store.searchLoading)

    const campaignOptions = computed(() => {
      const results = [...store.searchResults]
      if (props.editGroup && form.campaign_ids.length > 0) {
        form.campaign_ids.forEach((id) => {
          if (!results.find((c) => c.attributes.id === id)) {
            results.push({
              id: String(id),
              type: 'campaign',
              attributes: { id, name: `Campaign #${id}` }
            } as ICampaignMini)
          }
        })
      }
      return results
    })

    const resetForm = () => {
      form.name = ''
      form.campaign_ids = []
    }

    const searchCampaigns = (query: string) => {
      store.SearchCampaigns(query)
    }

    watch(
      () => props.editGroup,
      (newVal) => {
        if (newVal) {
          form.name = newVal.attributes.name
          form.campaign_ids = [...newVal.attributes.campaign_ids]
          if (form.campaign_ids.length > 0) {
            store.SearchCampaigns('')
          }
        } else {
          resetForm()
        }
      },
      { immediate: true }
    )

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal && !props.editGroup) {
          resetForm()
          store.SearchCampaigns('')
        }
      }
    )

    const handleClose = () => {
      visible.value = false
      resetForm()
    }

    const handleSave = async () => {
      if (!formRef.value) return

      try {
        await formRef.value.validate()
      } catch {
        return
      }

      loading.value = true
      try {
        if (isEdit.value && props.editGroup) {
          await store.UpdateItem(parseInt(props.editGroup.id), {
            name: form.name,
            campaign_ids: form.campaign_ids
          })
          ElMessage.success('Group updated successfully')
        } else {
          await store.CreateItem({
            name: form.name,
            campaign_ids: form.campaign_ids
          })
          ElMessage.success('Group created successfully')
        }
        emit('saved')
        handleClose()
      } catch (e: any) {
        ElMessage.error(e.message || 'Failed to save group')
      } finally {
        loading.value = false
      }
    }

    return {
      formRef,
      form,
      rules,
      visible,
      isEdit,
      loading,
      searchLoading,
      campaignOptions,
      searchCampaigns,
      handleClose,
      handleSave
    }
  }
})
</script>

<style lang="scss" scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
